import { useMemo, useState } from "react";
import { useEditorStore } from "../../stores/editorStore.js";
import type { GridBlock, GridRowDef } from "@juro/shared";
import { computeRowSpans, nextRowId } from "../../hooks/useTemplateEditor.js";
import { useEvaluatedGrid } from "../../hooks/useFormulaEval.js";
import { Button } from "../ui/Button.js";
import { FormulaBar } from "./FormulaBar.js";

type RowKind = "section" | "subsection" | "detail" | "subtotal";
type BusinessPreset = "fee" | "registration_tax" | "stamp" | "postage" | "withholding" | "consumption_tax";

const rowKindDefaults: Record<RowKind, string> = {
  section: "セクション",
  subsection: "サブ",
  detail: "明細",
  subtotal: "小計",
};

const presetLabels: Record<BusinessPreset, string> = {
  fee: "報酬",
  registration_tax: "登録免許税",
  stamp: "印紙代",
  postage: "郵送費",
  withholding: "源泉徴収",
  consumption_tax: "消費税",
};

const PROTECTED_COLUMN_IDS = new Set(["category", "qty", "amount"]);

function formatCell(value: unknown, format = "text"): string {
  if (value === null || value === undefined) return "";
  if (typeof value !== "number") return String(value);
  if (format === "currency") return value.toLocaleString("ja-JP");
  if (format === "percent") return `${Math.round(value * 1000) / 10}%`;
  return String(value);
}

function getRowKind(row: GridRowDef): RowKind {
  if (row.type === "subtotal") return "subtotal";
  if (row.is_section) return "section";
  if (row.is_subsection) return "subsection";
  return "detail";
}

function rowKind(row: GridRowDef): string {
  return row.display_label ?? rowKindDefaults[getRowKind(row)];
}

function taxLabel(row: GridRowDef): string {
  if (row.tax_category === "taxable") return "課税";
  if (row.tax_category === "non_taxable") return "非課税";
  if (row.tax_category === "pass_through") return "立替";
  return "未設定";
}

function makeBusinessRow(block: GridBlock, preset: BusinessPreset, parent?: string): GridRowDef {
  const id = nextRowId(block.rows, `r_${preset}`);
  const base: GridRowDef = {
    id,
    level: 3,
    parent,
    type: "row",
    display_label: presetLabels[preset],
    label: presetLabels[preset],
    preset,
    cells: {},
  };

  switch (preset) {
    case "fee":
      return { ...base, tax_category: "taxable", withholding_target: true, cells: { qty: 1, base: 0, rate: 0, note: "報酬" }, editable: ["category", "qty", "base", "rate", "note"] };
    case "registration_tax":
      return { ...base, tax_category: "pass_through", pass_through: true, cells: { qty: 1, base: 0, rate: 0, note: "登録免許税" }, editable: ["category", "qty", "base", "note"] };
    case "stamp":
      return { ...base, tax_category: "pass_through", pass_through: true, cells: { qty: 1, base: 0, rate: 0, note: "印紙代" }, editable: ["category", "qty", "base", "note"] };
    case "postage":
      return { ...base, tax_category: "non_taxable", cells: { qty: 1, base: 0, rate: 0, note: "郵送費" }, editable: ["category", "qty", "base", "note"] };
    case "withholding":
      return { ...base, tax_category: "non_taxable", display_label: "控除", label: "源泉徴収", cells: { amount: "=-withholding(sum(rows[*.subtotal].amount))", note: "源泉徴収" } };
    case "consumption_tax":
      return { ...base, tax_category: "non_taxable", display_label: "税", label: "消費税", cells: { amount: "=consumption_tax(sum(rows[*.subtotal].amount))", note: "消費税" } };
  }
}

export function GridEditor({ block }: { block: GridBlock }) {
  const setDraft = useEditorStore((state) => state.setDraft);
  const selectedCell = useEditorStore((state) => state.selectedCell);
  const selectCell = useEditorStore((state) => state.selectCell);
  const selectBlock = useEditorStore((state) => state.selectBlock);
  const evaluated = useEvaluatedGrid(block);
  const spans = useMemo(() => computeRowSpans(block.rows), [block.rows]);
  const [editing, setEditing] = useState<{ rowId: string; colId: string } | null>(null);
  const [draggingRowId, setDraggingRowId] = useState<string | null>(null);

  const selectedRow = selectedCell?.blockId === block.id
    ? block.rows.find((row) => row.id === selectedCell.rowId) ?? block.rows[0] ?? null
    : block.rows[0] ?? null;

  const selectedValue = useMemo(() => {
    if (!selectedCell || selectedCell.blockId !== block.id) return "";
    const row = block.rows.find((item) => item.id === selectedCell.rowId);
    const column = block.columns.find((item) => item.id === selectedCell.colId);
    const raw = row?.cells?.[selectedCell.colId] ?? column?.formula ?? "";
    return String(raw ?? "");
  }, [block, selectedCell]);

  function updateGrid(fn: (grid: GridBlock) => GridBlock) {
    setDraft((template) => ({
      ...template,
      body: {
        blocks: template.body.blocks.map((item) => item.id === block.id && item.type === "grid" ? fn(item) : item),
      },
    }));
  }

  function updateCell(rowId: string, colId: string, value: string) {
    updateGrid((grid) => ({
      ...grid,
      rows: grid.rows.map((row) => row.id === rowId
        ? colId === "category"
          ? { ...row, label: value, cells: { ...(row.cells ?? {}), [colId]: value } }
          : { ...row, cells: { ...(row.cells ?? {}), [colId]: value } }
        : row),
    }));
  }

  function updateSelectedRow(patch: Partial<GridRowDef>) {
    if (!selectedRow) return;
    updateGrid((grid) => ({
      ...grid,
      rows: grid.rows.map((row) => row.id === selectedRow.id ? { ...row, ...patch } : row),
    }));
  }

  function updateFormulaBar(value: string) {
    if (!selectedCell || selectedCell.blockId !== block.id) return;
    updateCell(selectedCell.rowId, selectedCell.colId, value);
  }

  function addRow(kind: RowKind) {
    const parent = selectedCell?.blockId === block.id ? selectedCell.rowId : undefined;
    const id = nextRowId(block.rows, kind === "subtotal" ? "r_subtotal" : "r");
    const row: GridRowDef = {
      id,
      level: kind === "section" ? 1 : kind === "subsection" ? 2 : 3,
      parent: kind === "section" ? undefined : parent,
      label: kind === "section" ? "新しいセクション" : kind === "subtotal" ? "小計" : "新しい明細",
      display_label: rowKindDefaults[kind],
      type: kind === "subtotal" ? "subtotal" : "row",
      is_section: kind === "section",
      is_subsection: kind === "subsection",
      cells: kind === "subtotal" ? { amount: parent ? `=sum(rows[${parent}.*].amount)` : "=sum(rows[*.subtotal].amount)" } : {},
    };
    updateGrid((grid) => ({ ...grid, rows: [...grid.rows, row] }));
  }

  function addBusinessRow(preset: BusinessPreset) {
    const parent = selectedCell?.blockId === block.id ? selectedCell.rowId : undefined;
    updateGrid((grid) => ({ ...grid, rows: [...grid.rows, makeBusinessRow(grid, preset, parent)] }));
  }

  function addColumn() {
    const id = `col_${block.columns.length + 1}`;
    updateGrid((grid) => ({ ...grid, columns: [...grid.columns, { id, header: "新しい列", width: 80 }] }));
  }

  function reorderRows(draggedId: string, targetId: string) {
    if (draggedId === targetId) return;
    updateGrid((grid) => {
      const from = grid.rows.findIndex((row) => row.id === draggedId);
      const to = grid.rows.findIndex((row) => row.id === targetId);
      if (from < 0 || to < 0) return grid;
      const rows = [...grid.rows];
      const [row] = rows.splice(from, 1);
      if (!row) return grid;
      rows.splice(to, 0, row);
      return { ...grid, rows };
    });
  }

  function deleteSelectedRow() {
    if (!selectedCell || selectedCell.blockId !== block.id) return;
    const idsToDelete = new Set<string>([selectedCell.rowId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const row of block.rows) {
        if (row.parent && idsToDelete.has(row.parent) && !idsToDelete.has(row.id)) {
          idsToDelete.add(row.id);
          changed = true;
        }
      }
    }
    updateGrid((grid) => ({ ...grid, rows: grid.rows.filter((row) => !idsToDelete.has(row.id)) }));
    selectBlock(block.id);
  }

  function deleteSelectedColumn() {
    if (!selectedCell || selectedCell.blockId !== block.id || block.columns.length <= 1) return;
    const targetId = selectedCell.colId;
    if (PROTECTED_COLUMN_IDS.has(targetId)) return;
    updateGrid((grid) => ({
      ...grid,
      columns: grid.columns.filter((column) => column.id !== targetId),
      rows: grid.rows.map((row) => {
        const { [targetId]: _removed, ...cells } = row.cells ?? {};
        return { ...row, cells };
      }),
      summary: grid.summary?.map((row) => {
        const { [targetId]: _removed, ...cells } = row.cells;
        return { ...row, cells };
      }),
    }));
    selectBlock(block.id);
  }

  function moveSelection(rowDelta: number, colDelta: number) {
    if (!selectedCell || selectedCell.blockId !== block.id) return;
    const rowIndex = block.rows.findIndex((row) => row.id === selectedCell.rowId);
    const colIndex = block.columns.findIndex((col) => col.id === selectedCell.colId);
    if (rowIndex < 0 || colIndex < 0) return;
    const nextRow = block.rows[Math.max(0, Math.min(block.rows.length - 1, rowIndex + rowDelta))];
    const nextCol = block.columns[Math.max(0, Math.min(block.columns.length - 1, colIndex + colDelta))];
    if (nextRow && nextCol) selectCell(block.id, nextRow.id, nextCol.id);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTableElement>) {
    if (!selectedCell || selectedCell.blockId !== block.id) return;
    const rowIndex = block.rows.findIndex((row) => row.id === selectedCell.rowId);
    const colIndex = block.columns.findIndex((col) => col.id === selectedCell.colId);
    if (rowIndex < 0 || colIndex < 0) return;
    if ((event.ctrlKey || event.metaKey) && (event.key === "Enter" || event.key.toLowerCase() === "d")) {
      event.preventDefault();
      addRow("detail");
      return;
    }
    if (event.key === "ArrowUp" || event.key === "ArrowDown" || event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      moveSelection(
        event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0,
        event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0,
      );
      return;
    }
    if (event.key === "F2") {
      event.preventDefault();
      setEditing({ rowId: selectedCell.rowId, colId: selectedCell.colId });
      return;
    }
    if (event.key === "Delete") {
      event.preventDefault();
      updateCell(selectedCell.rowId, selectedCell.colId, "");
      return;
    }
    if (event.key === "Tab" || event.key === "Enter") {
      event.preventDefault();
      const nextCol = event.key === "Enter" ? colIndex : event.shiftKey ? colIndex - 1 : colIndex + 1;
      const nextRow = event.key === "Enter"
        ? event.shiftKey ? rowIndex - 1 : rowIndex + 1
        : nextCol >= block.columns.length ? rowIndex + 1 : nextCol < 0 ? rowIndex - 1 : rowIndex;
      const normalizedCol = nextCol >= block.columns.length ? 0 : nextCol < 0 ? block.columns.length - 1 : nextCol;
      const row = block.rows[Math.max(0, Math.min(block.rows.length - 1, nextRow))];
      const col = block.columns[Math.max(0, Math.min(block.columns.length - 1, normalizedCol))];
      if (row && col) selectCell(block.id, row.id, col.id);
      return;
    }
  }

  const selectedColumn = selectedCell?.blockId === block.id
    ? block.columns.find((column) => column.id === selectedCell.colId) ?? null
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <FormulaBar
        block={block}
        selected={selectedCell?.blockId === block.id ? selectedCell : null}
        value={selectedValue}
        onChange={updateFormulaBar}
      />

      {selectedRow && (
        <section style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(130px, 1fr))", alignItems: "end", gap: 8, padding: 10, border: "1px solid #dbe3ea", borderRadius: 8, background: "#fff" }}>
          <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
            表示名
            <input
              value={selectedRow.display_label ?? rowKind(selectedRow)}
              onChange={(event) => updateSelectedRow({ display_label: event.target.value })}
              style={{ height: 32, border: "1px solid #cbd5e1", borderRadius: 7, padding: "0 10px", fontSize: 13, fontFamily: "inherit" }}
            />
          </label>
          <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
            税区分
            <select
              value={selectedRow.tax_category ?? ""}
              onChange={(event) => updateSelectedRow({ tax_category: event.target.value ? event.target.value as GridRowDef["tax_category"] : undefined })}
              style={{ height: 34, border: "1px solid #cbd5e1", borderRadius: 7, padding: "0 10px", fontSize: 13, fontFamily: "inherit", background: "#fff" }}
            >
              <option value="">未設定</option>
              <option value="taxable">課税</option>
              <option value="non_taxable">非課税</option>
              <option value="pass_through">立替金</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, height: 34, fontSize: 12, color: "#334155", fontWeight: 800 }}>
            <input
              type="checkbox"
              checked={Boolean(selectedRow.withholding_target)}
              onChange={(event) => updateSelectedRow({ withholding_target: event.target.checked })}
            />
            源泉対象
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, height: 34, fontSize: 12, color: "#334155", fontWeight: 800 }}>
            <input
              type="checkbox"
              checked={Boolean(selectedRow.pass_through)}
              onChange={(event) => updateSelectedRow({ pass_through: event.target.checked })}
            />
            立替金
          </label>
        </section>
      )}

      <section style={{ display: "flex", alignItems: "center", gap: 16, padding: "8px 12px", border: "1px solid #dbe3ea", borderRadius: 8, background: "#fff", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>グリッド設定</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
          <input
            type="checkbox"
            checked={!!block.bordered}
            onChange={(event) => updateGrid((g) => ({ ...g, bordered: event.target.checked }))}
          />
          枠線
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
          <input
            type="checkbox"
            checked={block.data_source === "line_items"}
            onChange={(event) => updateGrid((g) => ({ ...g, data_source: event.target.checked ? "line_items" : undefined }))}
          />
          明細から自動展開
        </label>
        {block.data_source === "line_items" && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
            縦ラベル
            <input
              value={block.row_group_label?.text ?? ""}
              placeholder="手続きの代理…"
              onChange={(event) => updateGrid((g) => ({
                ...g,
                row_group_label: event.target.value ? { text: event.target.value, vertical: true } : undefined,
              }))}
              style={{ height: 28, border: "1px solid #cbd5e1", borderRadius: 6, padding: "0 8px", fontSize: 12, width: 160 }}
            />
          </label>
        )}
      </section>

      <section style={{ display: "grid", gap: 8, padding: 10, border: "1px solid #dbe3ea", borderRadius: 8, background: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 900, minWidth: 72 }}>構造</span>
          <Button onClick={() => addRow("section")}>セクション行</Button>
          <Button onClick={() => addRow("subsection")}>サブ行</Button>
          <Button onClick={() => addRow("detail")}>明細行</Button>
          <Button onClick={() => addRow("subtotal")}>小計行</Button>
          <Button onClick={addColumn}>列追加</Button>
          <Button onClick={deleteSelectedRow} disabled={!selectedCell || selectedCell.blockId !== block.id}>行削除</Button>
          <Button
            onClick={deleteSelectedColumn}
            disabled={!selectedColumn || block.columns.length <= 1 || PROTECTED_COLUMN_IDS.has(selectedColumn.id)}
          >
            列削除
          </Button>
          {selectedColumn && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              列幅
              <input
                type="number"
                min={36}
                value={selectedColumn.width}
                onChange={(event) => {
                  const width = Math.max(36, Number(event.target.value) || selectedColumn.width);
                  updateGrid((grid) => ({
                    ...grid,
                    columns: grid.columns.map((column) => column.id === selectedColumn.id ? { ...column, width } : column),
                  }));
                }}
                style={{ width: 72, height: 30, border: "1px solid #cbd5e1", borderRadius: 7, padding: "0 8px" }}
              />
            </label>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 900, minWidth: 72 }}>業務項目</span>
          <Button onClick={() => addBusinessRow("fee")}>報酬</Button>
          <Button onClick={() => addBusinessRow("registration_tax")}>登録免許税</Button>
          <Button onClick={() => addBusinessRow("stamp")}>印紙代</Button>
          <Button onClick={() => addBusinessRow("postage")}>郵送費</Button>
          <Button onClick={() => addBusinessRow("withholding")}>源泉徴収</Button>
          <Button onClick={() => addBusinessRow("consumption_tax")}>消費税</Button>
        </div>
      </section>

      <div style={{ overflow: "auto", border: "1px solid #dbe3ea", borderRadius: 8, background: "#fff" }}>
        <table
          role="grid"
          aria-label={`${block.label ?? "明細"}の編集グリッド`}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 12 }}
        >
          <thead>
            <tr role="row" aria-rowindex={1}>
              <th style={{ width: 92, background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0", padding: 8 }}>種別</th>
              {block.columns.map((column, columnIndex) => (
                <th key={column.id} role="columnheader" aria-colindex={columnIndex + 2} style={{ width: column.width, background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", padding: 8, textAlign: "left" }}>
                  {column.header}
                  {PROTECTED_COLUMN_IDS.has(column.id) && <span style={{ color: "#94a3b8", fontSize: 10, marginLeft: 4 }}>固定</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => {
              const values = evaluated[row.id] ?? {};
              return (
                <tr
                  key={row.id}
                  draggable
                  onDragStart={(event) => {
                    setDraggingRowId(row.id);
                    event.dataTransfer.setData("text/plain", row.id);
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    reorderRows(event.dataTransfer.getData("text/plain") || draggingRowId || "", row.id);
                    setDraggingRowId(null);
                  }}
                  onDragEnd={() => setDraggingRowId(null)}
                  role="row"
                  aria-rowindex={rowIndex + 2}
                  style={{ background: row.is_section || row.is_subsection ? "#f8fafc" : row.type === "subtotal" ? "#f0fdfa" : "#fff", opacity: draggingRowId === row.id ? 0.55 : 1 }}
                >
                  <td
                    role="rowheader"
                    aria-colindex={1}
                    onClick={() => selectCell(block.id, row.id, block.columns[0]?.id ?? "category")}
                    style={{ border: "1px solid #e2e8f0", padding: "6px 8px", color: "#334155", fontWeight: 800, cursor: "pointer" }}
                  >
                    {rowKind(row)}
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>{taxLabel(row)} / span {spans.get(row.id) ?? 1}</div>
                  </td>
                  {block.columns.map((column, columnIndex) => {
                    const raw = column.id === "category" && row.label ? row.label : values[column.id];
                    const isFormula = typeof (row.cells?.[column.id] ?? column.formula) === "string" && String(row.cells?.[column.id] ?? column.formula).startsWith("=");
                    const isSelected = selectedCell?.blockId === block.id && selectedCell.rowId === row.id && selectedCell.colId === column.id;
                    const isEditing = editing?.rowId === row.id && editing.colId === column.id;
                    return (
                      <td
                        key={column.id}
                        role="gridcell"
                        aria-rowindex={rowIndex + 2}
                        aria-colindex={columnIndex + 2}
                        aria-selected={isSelected}
                        onClick={() => selectCell(block.id, row.id, column.id)}
                        onDoubleClick={() => setEditing({ rowId: row.id, colId: column.id })}
                        style={{
                          border: isSelected ? "2px solid #0f766e" : "1px solid #e2e8f0",
                          padding: isEditing ? 0 : "7px 8px",
                          background: isFormula ? "#f0fdf4" : "transparent",
                          textAlign: column.format === "currency" || column.format === "number" ? "right" : "left",
                          fontWeight: row.type === "subtotal" || row.is_section ? 700 : 500,
                          outline: "none",
                        }}
                      >
                        {isEditing ? (
                          <input
                            autoFocus
                            defaultValue={String(row.cells?.[column.id] ?? "")}
                            onBlur={(event) => { updateCell(row.id, column.id, event.target.value); setEditing(null); }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === "Tab") {
                                updateCell(row.id, column.id, event.currentTarget.value);
                                setEditing(null);
                              }
                              if (event.key === "Escape") setEditing(null);
                            }}
                            style={{ width: "100%", height: 32, border: "none", padding: "0 8px", boxSizing: "border-box", font: "inherit" }}
                          />
                        ) : (
                          <>
                            <span>{formatCell(raw, column.format)}</span>
                            {isFormula && <span style={{ display: "block", color: "#94a3b8", fontSize: 10, textAlign: "left" }}>fx</span>}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {block.summary && block.summary.length > 0 && (
        <section style={{ display: "grid", gap: 6, padding: 10, border: "1px solid #dbe3ea", borderRadius: 8, background: "#fff" }}>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>明細内の集計</div>
          {block.summary.map((row) => (
            <div key={row.id} style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 8, alignItems: "center", fontSize: 13 }}>
              <span style={{ fontWeight: row.emphasis ? 900 : 700, color: "#334155" }}>{row.label}</span>
              <code style={{ color: "#0f766e", background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: 6, padding: "5px 8px", textAlign: "right" }}>
                {String(row.cells.amount ?? "")}
              </code>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
